import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, distinctUntilChanged, map, of, Subject, switchMap } from 'rxjs';

import { BlazeTableTriggersService } from '@blaze-dashboard/blaze-table-feature';
import { TriggerInfo } from '@blaze-shared/api-contracts-domain';
import { TablesApiService } from '@blaze-shared/datasets-data-access';
import { ButtonBarComponent } from '@blaze-shared/shared-ui';
import { isDeepEqual } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

type Status = 'active' | 'inactive' | 'paused' | 'error';

@UntilDestroy()
@Component({
  selector: 'blaze-table-triggers-button',
  imports: [ButtonBarComponent, IconsModule],
  templateUrl: './blaze-table-triggers-button.component.html',
  styleUrl: './blaze-table-triggers-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlazeTableTriggersButtonComponent {
  private readonly blazeTableTriggersService = inject(BlazeTableTriggersService);
  private readonly tableApiService = inject(TablesApiService);

  tableId = input<string>('');
  triggers = input<TriggerInfo[] | undefined>([]);
  configTriggers = output<void>();

  private readonly updateTriggers$$ = new Subject<{
    tableId: string;
    triggers: TriggerInfo[] | undefined;
  }>();

  updatedTriggers = signal<TriggerInfo[]>([]);

  enabledCount = computed<number>(() => {
    const triggers = this.updatedTriggers();

    if (!triggers) {
      return 0;
    }

    return triggers.reduce<number>((acc, trigger) => {
      return this.blazeTableTriggersService.isTriggerActive(trigger) && !trigger.cycleDetected
        ? acc + 1
        : acc;
    }, 0);
  });

  status = computed<Status>(() => {
    const triggers = this.updatedTriggers() || [];

    if (!triggers.length) {
      return 'inactive';
    }

    if (triggers.some((trigger) => trigger.cycleDetected)) {
      return 'error';
    }

    if (this.enabledCount() === 0) {
      return 'paused';
    }

    return 'active';
  });

  constructor() {
    this.updateTriggers$$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(isDeepEqual),
        switchMap(({ tableId, triggers }) => {
          if (!tableId || !triggers?.length) {
            return of(null);
          }

          return this.tableApiService.postTriggersCycles(tableId).pipe(catchError(() => of(null)));
        }),
        map((table) => table?.triggers || []),
      )
      .subscribe((triggers) => {
        this.updatedTriggers.set(triggers);
      });

    effect(() => {
      const tableId = this.tableId();
      const triggers = this.triggers();
      this.updateTriggers$$.next({ tableId, triggers });
    });
  }
}
